import type { Task, TaskPerson, TaskStatus, TaskUrgency, UpdateTaskBody } from '@obras/shared';
import {
  formatDate,
  TASK_MAX_CHECKLIST_ITEMS,
  TASK_MAX_MEMBERS,
  TASK_URGENCIES,
  TASK_URGENCY_LABELS,
} from '@obras/shared';
import {
  Check,
  CircleAlert,
  Info,
  ListChecks,
  LoaderCircle,
  LogOut,
  MessageSquare,
  Plus,
  Trash2,
  type LucideIcon,
} from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { useState, type FormEvent, type ReactNode } from 'react';

import { EASE_OUT_QUINT } from '../../components/motion.js';
import { Button } from '../../components/ui/Button.js';
import { FormError } from '../../components/ui/Feedback.js';
import { SelectField, TextAreaField, TextField } from '../../components/ui/Field.js';
import { Modal } from '../../components/ui/Modal.js';
import { Tab, TabList } from '../../components/ui/Tabs.js';
import { ApiError } from '../../lib/api.js';
import { useAuth } from '../../lib/auth.js';
import { cn } from '../../lib/cn.js';
import { useOrgMembers, type OrgMember } from '../usuarios/api.js';
import {
  useAcrescentarItem,
  useAlterarItem,
  useAtualizarTarefa,
  useCriarTarefa,
  useExcluirTarefa,
  useIncluirPessoa,
  useTirarItem,
  useTirarPessoa,
} from './api.js';
import { Checklist, type ItemDoChecklist } from './Checklist.js';
import { Comentarios } from './Comentarios.js';
import { Equipe } from './Equipe.js';
import { EscolhaDaSituacao } from './Situacao.js';

const ICON = { strokeWidth: 1.5 } as const;

/*
 * O modal da tarefa (design.md §8, Tarefas), no tamanho maior do sistema
 * (`2xl`): a trilha de abas à esquerda — o desenho dos modais de usuário e de
 * obra — e o conteúdo em duas metades, cada uma rolando por si. Altura fixa a
 * partir de `sm`, cedendo à altura da tela: o modal não pula ao trocar de aba
 * nem quando a lista cresce. No celular, tudo empilhado.
 *
 * - "Checklist e comentários", a principal: o checklist numa metade e a
 *   conversa na outra.
 * - "Informações e equipe": título, descrição, situação, prioridade e prazo
 *   numa metade; a equipe na outra.
 */
const CORPO =
  'grid gap-6 px-6 pb-2 sm:h-[min(680px,calc(85dvh-10rem))] sm:grid-cols-[208px_minmax(0,1fr)]';
/** As duas metades: lado a lado do `md` em diante; abaixo, uma sob a outra, rolando juntas. */
const CONTEUDO =
  'grid min-h-0 gap-6 sm:overflow-y-auto md:grid-cols-2 md:grid-rows-[minmax(0,1fr)] md:overflow-visible';
const METADE = 'flex min-h-0 min-w-0 flex-col';
const SEGUNDA_METADE = cn(METADE, 'md:border-l md:border-line md:pl-6');
/** A metade de formulário rola inteira; as de lista rolam só a lista. */
const QUE_ROLA = 'gap-5 md:overflow-y-auto md:pr-1';

type Aba = 'andamento' | 'informacoes';

const ABAS: readonly { id: Aba; rotulo: string; icone: LucideIcon }[] = [
  { id: 'andamento', rotulo: 'Checklist e comentários', icone: ListChecks },
  { id: 'informacoes', rotulo: 'Informações e equipe', icone: Info },
];

/** Nas confirmações, o foco cai no botão que não perde nada. */
const FOCO_EM_FICAR = '[data-ficar]';
/** Na criação, o foco começa no título — o único campo obrigatório. */
const FOCO_NO_TITULO = '[data-foco-titulo]';

const PRIORIDADES = TASK_URGENCIES.map((urgencia) => ({
  value: urgencia,
  label: TASK_URGENCY_LABELS[urgencia],
}));

function mensagemDe(erro: unknown, padrao: string): string {
  return erro instanceof ApiError ? erro.message : padrao;
}

function pessoaDe(membro: OrgMember): TaskPerson {
  return { id: membro.id, name: membro.name, avatarUrl: membro.avatarUrl };
}

/** A trilha de abas, à esquerda; no celular, no topo. */
function Trilha({
  atual,
  onChange,
  comErro,
}: {
  atual: Aba;
  onChange: (aba: Aba) => void;
  comErro?: ReadonlySet<Aba> | undefined;
}) {
  return (
    <nav className="sm:sticky sm:top-0 sm:self-start">
      <TabList label="Partes da tarefa" orientation="vertical">
        {ABAS.map((aba) => (
          <Tab
            key={aba.id}
            vertical
            active={atual === aba.id}
            alert={comErro?.has(aba.id) ?? false}
            icon={aba.icone}
            onClick={() => onChange(aba.id)}
          >
            {aba.rotulo}
          </Tab>
        ))}
      </TabList>
    </nav>
  );
}

/** O conteúdo da aba: troca entrando de baixo, um fio, como nas abas da obra. */
function ConteudoDaAba({ aba, children }: { aba: Aba; children: ReactNode }) {
  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={aba}
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0, transition: { duration: 0.24, ease: EASE_OUT_QUINT } }}
        exit={{ opacity: 0, transition: { duration: 0.1 } }}
        className={CONTEUDO}
      >
        {children}
      </motion.div>
    </AnimatePresence>
  );
}

/** Os campos da tarefa: título, descrição, situação, prioridade e prazo. */
function CamposDaTarefa({
  titulo,
  onTitulo,
  onSairDoTitulo,
  erroDoTitulo,
  focoNoTitulo = false,
  descricao,
  onDescricao,
  onSairDaDescricao,
  status,
  onStatus,
  urgencia,
  onUrgencia,
  prazo,
  onPrazo,
  onSairDoPrazo,
}: {
  titulo: string;
  onTitulo: (valor: string) => void;
  onSairDoTitulo?: (() => void) | undefined;
  erroDoTitulo?: string | undefined;
  focoNoTitulo?: boolean | undefined;
  descricao: string;
  onDescricao: (valor: string) => void;
  onSairDaDescricao?: (() => void) | undefined;
  status: TaskStatus;
  onStatus: (status: TaskStatus) => void;
  urgencia: TaskUrgency;
  onUrgencia: (urgencia: TaskUrgency) => void;
  prazo: string;
  onPrazo: (valor: string) => void;
  onSairDoPrazo?: (() => void) | undefined;
}) {
  return (
    <>
      <TextField
        label="Título"
        required
        maxLength={200}
        placeholder="O que precisa ser feito"
        value={titulo}
        onChange={(evento) => onTitulo(evento.target.value)}
        onBlur={onSairDoTitulo}
        onKeyDown={(evento) => {
          if (onSairDoTitulo && evento.key === 'Enter') evento.currentTarget.blur();
        }}
        {...(focoNoTitulo ? { 'data-foco-titulo': '' } : {})}
        {...(erroDoTitulo ? { error: erroDoTitulo } : {})}
      />

      <TextAreaField
        label="Descrição"
        rows={6}
        maxLength={4000}
        placeholder="Detalhes, contexto, documentos — o que ajuda quem vai fazer"
        value={descricao}
        onChange={(evento) => onDescricao(evento.target.value)}
        onBlur={onSairDaDescricao}
      />

      <EscolhaDaSituacao value={status} onChange={onStatus} emGrade />

      <div className="grid gap-4 sm:grid-cols-2">
        <SelectField
          label="Prioridade"
          value={urgencia}
          onChange={(evento) => onUrgencia(evento.target.value as TaskUrgency)}
        >
          {PRIORIDADES.map((opcao) => (
            <option key={opcao.value} value={opcao.value}>
              {opcao.label}
            </option>
          ))}
        </SelectField>

        <TextField
          label="Prazo"
          type="date"
          value={prazo}
          onChange={(evento) => onPrazo(evento.target.value)}
          onBlur={onSairDoPrazo}
        />
      </div>
    </>
  );
}

/** Confirmação de um passo sem volta, com o botão de ficar em foco. */
function Confirmacao({
  open,
  titulo,
  descricao,
  ficar,
  confirmar,
  onFicar,
  onConfirmar,
  carregando = false,
}: {
  open: boolean;
  titulo: string;
  descricao: string;
  ficar: string;
  confirmar: ReactNode;
  onFicar: () => void;
  onConfirmar: () => void;
  carregando?: boolean | undefined;
}) {
  return (
    <Modal
      open={open}
      onClose={onFicar}
      title={titulo}
      description={descricao}
      focoInicial={FOCO_EM_FICAR}
      footer={
        <>
          <Button variant="ghost" data-ficar onClick={onFicar} disabled={carregando}>
            {ficar}
          </Button>
          <Button variant="danger" onClick={onConfirmar} loading={carregando}>
            {confirmar}
          </Button>
        </>
      }
    >
      {null}
    </Modal>
  );
}

// ───────────────────────────────────────────────────────── nova tarefa

/**
 * Criar: um formulário de verdade, com o "Criar tarefa" no pé. Abre em
 * "Informações e equipe", onde está o título; quem cria já está na equipe e
 * não sai; os itens do checklist ficam na tela até a tarefa existir, e a
 * conversa só começa depois. Fechar com algo preenchido pergunta antes de
 * descartar.
 */
export function ModalNovaTarefa({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { user } = useAuth();
  const pessoas = useOrgMembers();
  const criar = useCriarTarefa();

  const eu: TaskPerson = { id: user?.id ?? '', name: user?.name ?? '', avatarUrl: null };

  const [aba, setAba] = useState<Aba>('informacoes');
  const [titulo, setTitulo] = useState('');
  const [descricao, setDescricao] = useState('');
  const [status, setStatus] = useState<TaskStatus>('a_fazer');
  const [urgencia, setUrgencia] = useState<TaskUrgency>('normal');
  const [prazo, setPrazo] = useState('');
  const [membros, setMembros] = useState<readonly TaskPerson[]>([eu]);
  const [itens, setItens] = useState<readonly ItemDoChecklist[]>([]);
  const [erros, setErros] = useState<Record<string, string>>({});
  const [erro, setErro] = useState<string | null>(null);
  const [descartar, setDescartar] = useState(false);

  const preenchida =
    titulo.trim() !== '' || descricao.trim() !== '' || itens.length > 0 || membros.length > 1;
  // A aba que tem campo recusado ganha o ponto de aviso na trilha.
  const camposComErro = Object.keys(erros);
  const comErro = new Set<Aba>();
  if (camposComErro.some((campo) => campo.startsWith('checklist'))) comErro.add('andamento');
  if (camposComErro.some((campo) => !campo.startsWith('checklist'))) comErro.add('informacoes');

  function pedirParaFechar(): void {
    if (criar.isPending) return;
    if (preenchida) setDescartar(true);
    else onClose();
  }

  async function enviar(evento: FormEvent): Promise<void> {
    evento.preventDefault();
    setErro(null);

    if (titulo.trim().length < 2) {
      setErros({ title: 'Dê um título à tarefa' });
      setAba('informacoes');
      return;
    }
    setErros({});

    try {
      await criar.mutateAsync({
        title: titulo.trim(),
        ...(descricao.trim() ? { description: descricao.trim() } : {}),
        status,
        urgency: urgencia,
        ...(prazo ? { dueAt: prazo } : {}),
        memberIds: membros.filter((membro) => membro.id !== eu.id).map((membro) => membro.id),
        checklist: itens.map((item) => item.content),
      });
      onClose();
    } catch (falha) {
      if (falha instanceof ApiError) {
        const porCampo = falha.fieldErrors();
        setErros(porCampo);
        setErro(falha.details ? null : falha.message);
        if (Object.keys(porCampo).some((campo) => !campo.startsWith('checklist'))) {
          setAba('informacoes');
        }
      } else {
        setErro('Não foi possível criar a tarefa. Verifique sua conexão.');
      }
    }
  }

  return (
    <>
      {/* A confirmação vem antes no DOM: ao descartar, as duas fecham sem o foco cair no vazio. */}
      <Confirmacao
        open={descartar}
        titulo="Descartar a nova tarefa?"
        descricao="O que foi preenchido aqui não será guardado."
        ficar="Continuar editando"
        confirmar="Descartar"
        onFicar={() => setDescartar(false)}
        onConfirmar={() => {
          setDescartar(false);
          onClose();
        }}
      />

      <Modal
        open={open}
        onClose={pedirParaFechar}
        size="2xl"
        flush
        focoInicial={FOCO_NO_TITULO}
        title="Nova tarefa"
        description="Dê um título, monte a equipe e liste o que precisa ser feito."
        footer={
          <>
            <Button variant="ghost" onClick={pedirParaFechar} disabled={criar.isPending}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              type="submit"
              form="nova-tarefa"
              loading={criar.isPending}
              icon={<Plus {...ICON} size={16} />}
            >
              Criar tarefa
            </Button>
          </>
        }
      >
        <form
          id="nova-tarefa"
          onSubmit={(evento) => void enviar(evento)}
          noValidate
          className={CORPO}
        >
          <Trilha atual={aba} onChange={setAba} comErro={comErro} />

          <ConteudoDaAba aba={aba}>
            {aba === 'andamento' ? (
              <>
                <div className={METADE}>
                  <Checklist
                    itens={itens}
                    limite={TASK_MAX_CHECKLIST_ITEMS}
                    onAcrescentar={(texto) =>
                      setItens((atuais) => [
                        ...atuais,
                        { id: crypto.randomUUID(), content: texto, isDone: false },
                      ])
                    }
                    onReescrever={(id, texto) =>
                      setItens((atuais) =>
                        atuais.map((item) => (item.id === id ? { ...item, content: texto } : item)),
                      )
                    }
                    onTirar={(id) => setItens((atuais) => atuais.filter((item) => item.id !== id))}
                  />
                </div>
                <div className={SEGUNDA_METADE}>
                  <h3 className="text-[13px] font-medium">Comentários</h3>
                  <p className="mt-2 flex items-start gap-2 text-[13px] text-ink-3">
                    <MessageSquare {...ICON} size={15} className="mt-0.5 shrink-0" aria-hidden />A
                    conversa começa quando a tarefa existir: depois de criada, quem estiver na
                    equipe comenta aqui.
                  </p>
                </div>
              </>
            ) : (
              <>
                <div className={cn(METADE, QUE_ROLA)}>
                  {erro ? <FormError>{erro}</FormError> : null}
                  <CamposDaTarefa
                    titulo={titulo}
                    onTitulo={setTitulo}
                    erroDoTitulo={erros['title']}
                    focoNoTitulo
                    descricao={descricao}
                    onDescricao={setDescricao}
                    status={status}
                    onStatus={setStatus}
                    urgencia={urgencia}
                    onUrgencia={setUrgencia}
                    prazo={prazo}
                    onPrazo={setPrazo}
                  />
                </div>
                <div className={cn(SEGUNDA_METADE, QUE_ROLA)}>
                  <Equipe
                    membros={membros}
                    criadorId={eu.id}
                    euId={eu.id}
                    pessoasDoOrgao={pessoas.data?.items ?? []}
                    limite={TASK_MAX_MEMBERS}
                    onIncluir={(userId) => {
                      const pessoa = pessoas.data?.items.find((item) => item.id === userId);
                      if (pessoa) setMembros((atuais) => [...atuais, pessoaDe(pessoa)]);
                    }}
                    onTirar={(userId) =>
                      setMembros((atuais) => atuais.filter((membro) => membro.id !== userId))
                    }
                  />
                </div>
              </>
            )}
          </ConteudoDaAba>
        </form>
      </Modal>
    </>
  );
}

// ─────────────────────────────────────────────────────── tarefa aberta

/**
 * Ver é também editar: cada mudança grava na hora — a situação, a prioridade,
 * as pessoas e os itens no gesto; título, descrição e prazo ao sair do campo,
 * e ao fechar o modal, se ficou algo digitado. O pé diz se está salvando, se
 * salvou ou se algo falhou; não há "Salvar" para esquecer de apertar.
 *
 * Abre em "Checklist e comentários", a parte do dia a dia. Excluir é de quem
 * criou, e pergunta antes. Sair da tarefa (tirar a si mesmo) também pergunta:
 * a pessoa deixa de vê-la.
 */
export function ModalDaTarefa({
  tarefa,
  onClose,
}: {
  /** A tarefa aberta, viva na lista; ausente, o modal está fechado. */
  tarefa: Task | undefined;
  onClose: () => void;
}) {
  // Uma montagem por tarefa: os rascunhos dos campos nascem dela e morrem com ela.
  return tarefa ? <ConteudoDaTarefa key={tarefa.id} tarefa={tarefa} onClose={onClose} /> : null;
}

function ConteudoDaTarefa({ tarefa, onClose }: { tarefa: Task; onClose: () => void }) {
  const { user } = useAuth();
  const pessoas = useOrgMembers();
  const euId = user?.id ?? '';

  const atualizar = useAtualizarTarefa();
  const excluir = useExcluirTarefa();
  const incluir = useIncluirPessoa();
  const tirar = useTirarPessoa();
  const acrescentar = useAcrescentarItem();
  const alterar = useAlterarItem();
  const tirarItem = useTirarItem();

  const [aba, setAba] = useState<Aba>('andamento');
  const [titulo, setTitulo] = useState(tarefa.title);
  const [descricao, setDescricao] = useState(tarefa.description ?? '');
  const [prazo, setPrazo] = useState(tarefa.dueAt ?? '');
  const [erroDoTitulo, setErroDoTitulo] = useState<string | null>(null);
  const [falha, setFalha] = useState<string | null>(null);
  const [confirmarExclusao, setConfirmarExclusao] = useState(false);
  const [confirmarSaida, setConfirmarSaida] = useState(false);

  const salvando = [atualizar, incluir, tirar, acrescentar, alterar, tirarItem].some(
    (mutacao) => mutacao.isPending,
  );

  /** Os retornos de cada gravação: a falha aparece no pé; o próximo acerto a limpa. */
  const avisos = {
    onSuccess: () => setFalha(null),
    onError: (erro: unknown) =>
      setFalha(mensagemDe(erro, 'Não foi possível salvar. Tente de novo.')),
  };

  function salvar(body: UpdateTaskBody): void {
    atualizar.mutate({ id: tarefa.id, ...body }, avisos);
  }

  /** Grava o que ficou digitado nos campos de texto; vale ao sair de cada um e ao fechar. */
  function gravarTitulo(): void {
    const texto = titulo.trim();
    if (texto === tarefa.title) {
      setErroDoTitulo(null);
      return;
    }
    if (texto.length < 2) {
      setErroDoTitulo('Dê um título à tarefa');
      return;
    }
    setErroDoTitulo(null);
    salvar({ title: texto });
  }

  function gravarDescricao(): void {
    if (descricao.trim() !== (tarefa.description ?? '')) salvar({ description: descricao.trim() });
  }

  function gravarPrazo(): void {
    if (prazo !== (tarefa.dueAt ?? '')) salvar({ dueAt: prazo || null });
  }

  function fechar(): void {
    if (titulo.trim().length >= 2) gravarTitulo();
    gravarDescricao();
    gravarPrazo();
    onClose();
  }

  const concluida = tarefa.completedAt ? ` · concluída em ${formatDate(tarefa.completedAt)}` : '';

  return (
    <>
      <Confirmacao
        open={confirmarExclusao}
        titulo="Excluir a tarefa?"
        descricao={`"${tarefa.title}" sai da lista de todos que participam dela.`}
        ficar="Manter a tarefa"
        confirmar="Excluir"
        carregando={excluir.isPending}
        onFicar={() => setConfirmarExclusao(false)}
        onConfirmar={() =>
          excluir.mutate(tarefa.id, {
            onSuccess: () => setConfirmarExclusao(false),
            onError: (erro) => {
              setConfirmarExclusao(false);
              setFalha(mensagemDe(erro, 'Não foi possível excluir a tarefa.'));
            },
          })
        }
      />
      <Confirmacao
        open={confirmarSaida}
        titulo="Sair da tarefa?"
        descricao="Você deixa de ver esta tarefa. Quem continua nela pode incluir você de novo."
        ficar="Continuar na tarefa"
        confirmar={
          <>
            <LogOut {...ICON} size={16} aria-hidden />
            Sair
          </>
        }
        carregando={tirar.isPending}
        onFicar={() => setConfirmarSaida(false)}
        onConfirmar={() =>
          tirar.mutate(
            { id: tarefa.id, userId: euId },
            {
              onSuccess: () => setConfirmarSaida(false),
              onError: (erro) => {
                setConfirmarSaida(false);
                setFalha(mensagemDe(erro, 'Não foi possível sair da tarefa.'));
              },
            },
          )
        }
      />

      <Modal
        open
        onClose={fechar}
        size="2xl"
        flush
        title={tarefa.title}
        description={`Criada por ${tarefa.createdBy.name} em ${formatDate(tarefa.createdAt)}${concluida}`}
        footer={
          /*
           * No computador, uma linha: excluir à esquerda, o aviso de gravação
           * e o fechar à direita. No celular o aviso sobe para uma linha só
           * dele, e os dois botões ficam nas pontas da de baixo.
           */
          <div className="flex w-full flex-wrap items-center gap-x-3 gap-y-2">
            {tarefa.canDelete ? (
              <Button
                variant="danger"
                onClick={() => setConfirmarExclusao(true)}
                icon={<Trash2 {...ICON} size={16} />}
                className="sm:order-1"
              >
                Excluir
              </Button>
            ) : null}

            <span
              aria-live="polite"
              className="order-first flex w-full items-center gap-1.5 text-[12px] text-ink-3 sm:order-2 sm:ml-auto sm:w-auto"
            >
              {falha ? (
                <>
                  <CircleAlert {...ICON} size={14} className="text-danger" aria-hidden />
                  <span className="text-danger">{falha}</span>
                </>
              ) : salvando ? (
                <>
                  <LoaderCircle {...ICON} size={14} className="animate-spin" aria-hidden />
                  Salvando…
                </>
              ) : (
                <>
                  <Check {...ICON} size={14} className="text-success" aria-hidden />
                  Alterações salvas na hora
                </>
              )}
            </span>

            <Button onClick={fechar} className="ml-auto sm:order-3 sm:ml-0">
              Fechar
            </Button>
          </div>
        }
      >
        <div className={CORPO}>
          <Trilha
            atual={aba}
            onChange={setAba}
            comErro={erroDoTitulo ? new Set<Aba>(['informacoes']) : undefined}
          />

          <ConteudoDaAba aba={aba}>
            {aba === 'andamento' ? (
              <>
                <div className={METADE}>
                  <Checklist
                    itens={tarefa.checklist}
                    limite={TASK_MAX_CHECKLIST_ITEMS}
                    onAcrescentar={(content) =>
                      acrescentar.mutate({ id: tarefa.id, content }, avisos)
                    }
                    onAlternar={(itemId, feito) =>
                      alterar.mutate({ id: tarefa.id, itemId, isDone: feito }, avisos)
                    }
                    onReescrever={(itemId, content) =>
                      alterar.mutate({ id: tarefa.id, itemId, content }, avisos)
                    }
                    onTirar={(itemId) => tirarItem.mutate({ id: tarefa.id, itemId }, avisos)}
                  />
                </div>
                <div className={SEGUNDA_METADE}>
                  <Comentarios taskId={tarefa.id} />
                </div>
              </>
            ) : (
              <>
                <div className={cn(METADE, QUE_ROLA)}>
                  <CamposDaTarefa
                    titulo={titulo}
                    onTitulo={setTitulo}
                    onSairDoTitulo={gravarTitulo}
                    erroDoTitulo={erroDoTitulo ?? undefined}
                    descricao={descricao}
                    onDescricao={setDescricao}
                    onSairDaDescricao={gravarDescricao}
                    status={tarefa.status}
                    onStatus={(status) => salvar({ status })}
                    urgencia={tarefa.urgency}
                    onUrgencia={(urgency) => salvar({ urgency })}
                    prazo={prazo}
                    onPrazo={setPrazo}
                    onSairDoPrazo={gravarPrazo}
                  />
                </div>
                <div className={cn(SEGUNDA_METADE, QUE_ROLA)}>
                  <Equipe
                    membros={tarefa.members}
                    criadorId={tarefa.createdBy.id}
                    euId={euId}
                    pessoasDoOrgao={pessoas.data?.items ?? []}
                    limite={TASK_MAX_MEMBERS}
                    onIncluir={(userId) => incluir.mutate({ id: tarefa.id, userId }, avisos)}
                    onTirar={(userId) =>
                      userId === euId
                        ? setConfirmarSaida(true)
                        : tirar.mutate({ id: tarefa.id, userId }, avisos)
                    }
                  />
                </div>
              </>
            )}
          </ConteudoDaAba>
        </div>
      </Modal>
    </>
  );
}
